import { useAuth } from 'auth/context/AuthContext';
import { useCallback, useMemo } from 'react';

import {
  Persona_Inquiry_Status_Enum,
  useGetAgreementsByUserIdQuery,
  useGetUsersVerificationStatusByEmailSubscription,
  UserAgreementTypes,
} from 'graphql/generated/hasura';
import { Roles } from '../firebase/interfaces';
import { useGetUserVerifyStatuses } from './useGetUserVerifyStatuses';

export interface UserVerificationStatus {
  status: Persona_Inquiry_Status_Enum | null | undefined;
}

const currentAgreementTypes = [
  UserAgreementTypes.Baa,
  UserAgreementTypes.Hippa,
];

const useUserVerificationStatus = () => {
  const { user: loggedUser } = useAuth();

  const {
    data: userVerficationData,
    loading: userVerificationLoading,
    error: errorUserVerification,
  } = useGetUsersVerificationStatusByEmailSubscription({
    fetchPolicy: 'network-only',
    skip: !loggedUser?.role || loggedUser?.role !== Roles.PATIENT,
    variables: {
      email: loggedUser?.email || '',
    },
  });

  const userVerificationValidation = useCallback(async () => {
    let userVerificationStatus = null;

    userVerificationStatus = userVerficationData?.users[0].kyc_current_status;

    return {
      userVerificationStatus,
    };
  }, [userVerficationData?.users]);

  return useMemo(
    () => ({
      userVerificationValidation,
      loading: userVerificationLoading,
      error: errorUserVerification,
    }),
    [
      userVerificationValidation,
      userVerificationLoading,
      errorUserVerification,
    ],
  );
};

export const useGetUsersVerificationStatusesByEmail = () => {
  const { user: loggedUser } = useAuth();

  const { userVerficationData, loading, error } = useGetUserVerifyStatuses();

  const { data: userAgreements, previousData } = useGetAgreementsByUserIdQuery({
    fetchPolicy: 'cache-and-network',
    variables: { id: loggedUser?.uuid },
  });

  const data = useMemo(() => {
    const { kyc_current_status, kyc_insurance_status, insurance_verify_later } =
      userVerficationData?.users[0] ?? {};
    const agreements = (
      (userAgreements ?? previousData)?.user_agreements ?? []
    ).reduce(
      (acc, { agreement_type, id, status }) => ({
        ...acc,
        [agreement_type]: { id, status },
      }),
      {} as Record<
        UserAgreementTypes,
        { id: number; status: Persona_Inquiry_Status_Enum }
      >,
    );
    return {
      verifyId: kyc_current_status,
      insuranceId: kyc_insurance_status,
      later: insurance_verify_later,
      baa: agreements?.[UserAgreementTypes.Baa],
      hippa: agreements?.[UserAgreementTypes.Hippa],
    };
  }, [userVerficationData, userAgreements, previousData]);

  const count = useMemo(() => {
    let agreements = (userAgreements?.user_agreements ?? []).filter((agr) =>
      currentAgreementTypes.includes(agr?.agreement_type),
    ).length;
    const { kyc_current_status, kyc_insurance_status, insurance_verify_later } =
      userVerficationData?.users[0] ?? {};
    if (kyc_insurance_status === Persona_Inquiry_Status_Enum.Approved) {
      agreements++;
    }
    if (kyc_current_status === Persona_Inquiry_Status_Enum.Approved) {
      agreements++;
    }
    if (insurance_verify_later) {
      agreements++;
    }
    return agreements;
  }, [userAgreements, userVerficationData]);

  return {
    error,
    loading,
    data,
    count,
  };
};

export default useUserVerificationStatus;
